import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DA_QuantityPicker } from "@molecules/QuantityPicker/QuantityPicker.component";
import { DA_CartButton } from "@molecules/CartButton/CartButton.component";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { DA_BasicLink } from "@danishagro/shared/src/components/atoms/BasicLink/BasicLink.component";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { useScreen } from "@danishagro/shared/src/contexts/screen.context";
import { useCart } from "@danishagro/shared/src/contexts/cart/cart.context";
import { useNavigate } from "react-router-dom";
import { DA_Text } from "@danishagro/shared/src/components/atoms/Text/Text.component";
import { formatNumber } from "@danishagro/shared/src/helpers/formatNumber.helper";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { DA_NewProductSimple } from "@danishagro/shared/src/interfaces/productv2.interface";
import { ModalSize, useModal } from "@danishagro/shared/src/contexts/modal.context";
import { useHidePrice } from "@hooks/useHidePrice.hook";
import { DA_DeliveryInfoModalContent } from "@templates/DeliveryInfoModal/DeliveryInfoModal.component";
import { DA_WishListButton } from "@molecules/WishListButtonSiloAndHouse/WishListButton.component";
import { useProductTracker } from "@danishagro/shared/src/gtm/useProductsTracker";
import { DA_InlineLink } from "@danishagro/shared/src/components/atoms/InlineLink/InlineLink.component";
import { B2bImageSrc } from "@danishagro/shared/src/helpers/imageSrc.helper";
import { ImageConfig } from "@danishagro/shared/src/content/imageConfigs/imageConfig.enum";
import { useBottomSheet } from "@danishagro/shared/src/contexts/bottomSheet/bottomSheet.context";
import { DA_QuantityOnPalletOverlay } from "@danishagro/shared/src/modals/QuantityOnPalletShop/Overlay/Overlay.component";
import { DA_Modal } from "@danishagro/shared/src/components/molecules/Modal/Modal.component";
import { modalState } from "@danishagro/shared/src/enums/modalState.enum";
import { BottomSheetDeliveryInfo } from "@organisms/BottomSheetDeliveryInfo";
import { useDynamicOverlay } from "@danishagro/shared/src/hooks/dynamicOverlay/useDynamicOverlay.hook";
import { DA_IconNames } from "@danishagro/shared/src/components/atoms/Icon/Icon.component";
import { getRelativeRootOnLocalhost } from "@danishagro/shared/src/helpers/getRelativeUrlOnLocalhost.helper";
import { B2B_SITE_ORIGIN } from "@danishagro/shared/src/constants/urlRoot.constants";
import { DA_Button } from "@danishagro/shared/src/components/atoms/Button/Button.component";
import { useFavoriteList } from "@danishagro/shared/src/hooks/api/useFavoriteList.hook";
import { useFavorites } from "@danishagro/shared/src/contexts/favorites.context";
import toast from "react-hot-toast";
import { DA_Toast } from "@danishagro/shared/src/components/molecules/Toast/Toast.component";
import { adjustUrl } from "@danishagro/shared/src/helpers/adjustUrl.helper";
import { getDiscountedVariantUrl } from "@helpers/getDiscountedVariantUrl.helper";
import { DA_SalesPriceBase } from "@danishagro/shared/src/interfaces/price.interface";
import { DA_Tag } from "@danishagro/shared/src/components/atoms/Tag/Tag.component";
import { DA_Image } from "@danishagro/shared/src/components/atoms/Image/Image.component";
import { useMaxValue } from "@hooks/useMaxValue.hook";
import { DA_ProductListPrice } from "./components/ProductListPrice/ProductListPrice.component";
import S from "./ProductListItemV1.module.scss";

export type V1Props = DA_NewProductSimple & {
    categoryId?: string; // TODO: DELETE DEBUG DATA
    eagerLoading?: boolean;
    isFavorite?: boolean;

    "data-id"?: string;
    "data-name"?: string;
    "data-listid"?: string;
    "data-price"?: string;
    "data-category"?: string;
    "data-brand"?: string;

    allVariants?: Array<{
        variantId: string;
    }>;
    fetchPriority?: boolean;
};

export const V1 = React.forwardRef<HTMLElement, V1Props>((props, ref) => {
    const {
        entityId,
        hasVariants,
        variantEntityId,
        displayName,
        newProduct,
        imageUrl,
        eagerLoading,
        url,
        productUrl,
        hasHazardStatement,
        quantityOnPallet,
    } = props;

    const hasSpecialFreightInformation =
        props.relewiseProduct?.allVariants?.[0]?.data?.specialFreightInformation?.value;
    const specialFreightInformationText =
        props.relewiseProduct?.allVariants?.[0]?.data?.specialFreightDescription?.value;
    const minimumSalesQuantity =
        props.relewiseProduct?.allVariants?.[0]?.data?.minimumSalesQuantity?.value;

    const navigate = useNavigate();
    const [quantity, setQuantity] = useState(1);
    const [awaitingResponse, setAwaitingResponse] = useState(false);

    const { showModal } = useModal();
    const hidePrice = useHidePrice();
    const { showOverlay } = useDynamicOverlay();
    const { isMobileOrTablet } = useScreen();
    const { isFarmInTimeEnabled, showPrices, displayFavoritesList, customerNumber } = useAppData();
    const { getDictionaryString } = useTranslations();
    const { addToCart } = useCart();
    const [choosedValue, setChoosedValue] = useState(false);
    const { presentBottomSheet } = useBottomSheet();
    const [salesEndDate, setSalesEndDate] = useState<DA_SalesPriceBase>(
        props.price && props.price?.quantityDiscounts[0]?.discount
    );
    const { siteVariant } = useAppData();
    const [modalViewState, setModalViewState] = useState<modalState>(modalState.hide);
    const [numberOfPallets, setNumberOfPallets] = useState<number>(0);
    const [totalPalletQuantity, setTotalPalletQuantity] = useState<number>(0);

    const isAvailableToBuy = useRef(!props.data?.redirectUrl?.value).current;
    const highMax = useMaxValue(siteVariant);

    useEffect(() => {
        if (props.price?.quantityDiscounts[0]?.discount)
            setSalesEndDate(props.price?.quantityDiscounts[0]?.discount);
    }, [props.price]);

    let favorites = undefined;
    let refreshFavorites = () => {};

    const favoritesListUrl = `${getRelativeRootOnLocalhost(B2B_SITE_ORIGIN)}/favorites`;

    // Helper function to get relative URL on localhost
    const getLocalUrl = (url: string) => {
        if (window.location.hostname === "localhost") {
            return productUrl || adjustUrl(url);
        }
        return adjustUrl(url);
    };

    // Wrap the useFavorites call in a try-catch block to avoid errors if the provider is not available
    try {
        const favoritesContext = useFavorites();
        favorites = displayFavoritesList ? favoritesContext.favorites : undefined;
        refreshFavorites = displayFavoritesList ? favoritesContext.refreshFavorites : () => {};
    } catch (error) {
        // If useFavorites fails (because the provider is not available), use default values
        favorites = undefined;
        refreshFavorites = () => {}; // No-op function as a fallback
    }

    const [isFavorite, setIsFavorite] = useState(props.isFavorite);

    const deliveryModalTitle = getDictionaryString(
        hasSpecialFreightInformation ? "deliveryInfoOverwrite" : "deliveryInfo"
    );

    const { addProductToFavorites, removeProductFromFavorites, loading } = useFavoriteList(
        customerNumber,
        customerNumber
    );

    // Shipping modal
    const openShippingInfoModal = useCallback(() => {
        showModal(
            <DA_DeliveryInfoModalContent
                specialFreightOverlayText={specialFreightInformationText}
            />,
            {
                size: ModalSize.XXL,
            }
        );
    }, [showModal, specialFreightInformationText]);

    const openDynamicOverlay = useCallback(() => {
        showOverlay({
            id: "delivery-filter_open",
            title: deliveryModalTitle,
            content: (
                <BottomSheetDeliveryInfo
                    version="v1"
                    specialFreightOverlayText={specialFreightInformationText}
                />
            ),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showOverlay]);

    const formatPrice = useCallback(
        (price: number) => formatNumber(price, "da-DK", { decimals: 2 }),
        []
    );

    const {
        "data-id": dataId,
        "data-name": dataName,
        "data-listid": dataListId,
        "data-category": dataCategory,
        "data-brand": dataBrand,
        // ... rest of your props
    } = props;

    // Extract the category from the URL
    const category = props?.url?.split("/").slice(0, -1).join("_").replace(/^_/, "");

    // GTM Tracking product clicked
    const trackProductClick = useProductTracker({
        ...props,
        price: formatPrice(props?.price?.priceWithoutVat),
        currency: props?.price?.currency || "DKK",
        quantity: quantity,
        category: category,
        name: displayName,
        brand: dataBrand,
    });

    const latestQuantityRef = useRef(quantity);
    const quantityAsInt = parseInt(quantityOnPallet?.value);

    useEffect(() => {
        latestQuantityRef.current = quantity;
    }, [quantity]);

    const calculateRangeOffset = (quantityOnPallet: number) => {
        return Math.floor(quantityOnPallet * 0.25); // ±25% of quantityOnPallet
    };

    useEffect(() => {
        const intValue = quantity;
        setModalViewState(modalState.hide);

        if (!isNaN(quantity)) {
            const rangeOffset = calculateRangeOffset(quantityAsInt);
            for (let numberOfPallets = 1; numberOfPallets <= 100; numberOfPallets++) {
                const totalPalletQuantity = numberOfPallets * quantityAsInt;
                const lowerBound = totalPalletQuantity - rangeOffset || 0;
                const upperBound = totalPalletQuantity + rangeOffset || 0;

                if (
                    intValue !== totalPalletQuantity &&
                    intValue >= lowerBound &&
                    intValue <= upperBound
                ) {
                    setModalViewState(modalState.show);
                    setNumberOfPallets(numberOfPallets);
                    setTotalPalletQuantity(totalPalletQuantity);
                    return;
                } else if (intValue === totalPalletQuantity) {
                    setModalViewState(modalState.hide);
                    return;
                }
            }
        }
    }, [quantity, quantityAsInt]);

    const check = useCallback(
        (
            showModalState: modalState,
            defaultQuantityOnPallet: number,
            totalPalletQuantity: number,
            numberOfPallets: number
        ) => {
            return new Promise<number>((resolve) => {
                if (showModalState === modalState.show && !choosedValue) {
                    const handleSelection = (selectedValue) => {
                        setQuantity(selectedValue);
                        setChoosedValue(true);
                        resolve(selectedValue); // Resolve the promise after the state is updated with the new quantity
                    };

                    if (isMobileOrTablet) {
                        presentBottomSheet(
                            <DA_QuantityOnPalletOverlay
                                quantity={latestQuantityRef.current}
                                quantityOnPallet={totalPalletQuantity}
                                defaultQuantityOnPallet={defaultQuantityOnPallet}
                                numberOfPallets={numberOfPallets}
                                onSelection={handleSelection}
                            />,
                            {
                                id: "shop-quantity-bottom-sheet",
                                contentPadding: true,
                                headerTitle: getDictionaryString("shopquantityonpallettitle"),
                                snapPoints: ({ minHeight }) => [minHeight],
                                hideCloseButton: true,
                            }
                        );
                    } else {
                        showModal(
                            <DA_Modal title={getDictionaryString("shopquantityonpallettitle")}>
                                <DA_QuantityOnPalletOverlay
                                    quantity={latestQuantityRef.current}
                                    quantityOnPallet={totalPalletQuantity}
                                    defaultQuantityOnPallet={quantityAsInt}
                                    numberOfPallets={numberOfPallets}
                                    onSelection={handleSelection}
                                />
                            </DA_Modal>,
                            {
                                size: ModalSize.XS,
                                shouldCloseOnEsc: false,
                                shouldCloseOnOverlayClick: false,
                            }
                        );
                    }
                } else {
                    resolve(latestQuantityRef.current); // Resolve immediately if no modal is shown
                }
            });
        },
        [
            choosedValue,
            getDictionaryString,
            isMobileOrTablet,
            presentBottomSheet,
            quantityAsInt,
            showModal,
        ]
    );

    // Navigate to the first variant that has a discount
    const discountedVariantUrl = useMemo(
        () =>
            getDiscountedVariantUrl(
                props.price?.variantDiscounts,
                props.relewiseProduct?.data?.canonicalBaseUrl?.value,
                props.relewiseProduct?.allVariants
            ),
        [
            props.price?.variantDiscounts,
            props.relewiseProduct?.data?.canonicalBaseUrl?.value,
            props.relewiseProduct?.allVariants,
        ]
    );

    const addProductToCart = useCallback(
        async (modalViewState, defaultQuantityOnPallet, totalPalletQuantity, numberOfPallets) => {
            const selectedQuantity = await check(
                modalViewState,
                defaultQuantityOnPallet,
                totalPalletQuantity,
                numberOfPallets
            ); // Wait for check to complete and get the latest quantity

            setAwaitingResponse(true);
            if (hasHazardStatement || hasVariants) {
                navigate(discountedVariantUrl || url);
            } else {
                addToCart({
                    productId: variantEntityId,
                    quantity: selectedQuantity,
                }).finally(() => setAwaitingResponse(false));
            }
            // GTM Tracking
            trackProductClick("add_to_cart");
        },
        [
            addToCart,
            check,
            discountedVariantUrl,
            hasHazardStatement,
            hasVariants,
            navigate,
            trackProductClick,
            url,
            variantEntityId,
        ]
    );

    const onLinkClicked = () => {
        if (typeof window !== "undefined") {
            navigate(`${window.location.pathname}${window.location.search}`, {
                replace: true,
                state: {
                    scrollY: document.documentElement.scrollTop || document.body.scrollTop,
                },
            });
        }
        // // GTM Tracking the product click when the link is clicked
        trackProductClick("select_item");
    };

    // Update local isFavorite state when the global favorites change
    // Inside your component
    const favoritesRef = useRef(favorites);

    useEffect(() => {
        // Keep the ref updated with the latest favorites value
        favoritesRef.current = favorites;
    }, [favorites]);

    useEffect(() => {
        if (!displayFavoritesList) return;

        if (favorites) {
            let isInFavorites = false;

            // Check if the product has variants and multiple variants
            if (props.hasVariants && props.relewiseProduct?.allVariants?.length > 1) {
                // Check if any variant's productVariantId is in the favorites
                isInFavorites = props.relewiseProduct.allVariants.some((variant) =>
                    favorites.has(variant.variantId)
                );
            } else {
                // If no variants or only one variant, check if the productId is in the favorites
                isInFavorites = favorites.has(props.variantEntityId);
            }

            setIsFavorite(isInFavorites);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [favorites, props.entityId, props.hasVariants, props.relewiseProduct?.allVariants]);

    // Function to handle adding/removing the item from favorites
    const handleFavoriteToggle = useCallback(() => {
        const action = isFavorite ? removeProductFromFavorites : addProductToFavorites;
        const toastText = isFavorite
            ? getDictionaryString("ToastRemovedFromFavoritesText")
            : getDictionaryString("ToastAddedToFavoritesText");

        action({
            productId: entityId,
            variantId: variantEntityId,
        }).then(() => {
            refreshFavorites();
            toast.custom(() => (
                <DA_Toast
                    href={favoritesListUrl}
                    goTo={getDictionaryString("showFavorites")}
                    altStyle={true}
                    message={
                        <>
                            <div>{toastText}</div>
                            {displayName}
                        </>
                    }
                />
            ));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isFavorite,
        props.entityId,
        props.variantEntityId,
        addProductToFavorites,
        removeProductFromFavorites,
        refreshFavorites,
    ]);

    const calculatePrice = useCallback(() => {
        if (props.price?.quantityDiscounts?.length > 0) {
            const applicableDiscount = props.price.quantityDiscounts.find(
                (item) => quantity >= item.quantityFrom && quantity <= (item.quantityTo || Infinity)
            );
            return applicableDiscount ? applicableDiscount.price : props.price.priceWithoutVat || 0;
        }
        return props.price?.priceWithoutVat || 0;
    }, [props.price, quantity]);

    const calculatedPrice = calculatePrice();

    return (
        <article
            className={cn(S.productListItem, newProduct && S.productListItemNew)}
            ref={ref}
            data-id={dataId}
            data-category={dataCategory}
            data-brand={dataBrand}
            key={variantEntityId}
            data-name={dataName}
            data-listid={dataListId}
            data-price={formatPrice(calculatedPrice)}
        >
            <div className={S.imageWrapper}>
                {isFarmInTimeEnabled && globalThis.isAuthenticated && isAvailableToBuy && (
                    <div className={S.wishListButton}>
                        <DA_WishListButton
                            productId={entityId}
                            productName={dataName}
                            productImage={B2bImageSrc(imageUrl, ImageConfig.CategoryImage)}
                            variantId={variantEntityId}
                            productPageUrl={url}
                            quantityOnPallet={props.data?.quantityOnPallet?.value}
                            productNumber={props.masterProductNumber}
                            price={props.price?.priceWithoutVat}
                            minimumSalesQuantity={minimumSalesQuantity}
                            hasVariants={hasVariants}
                            isVariantSelected={() => {
                                navigate(getLocalUrl(url));
                            }}
                        />
                    </div>
                )}

                <div className={cn(S.favBtn, isFavorite && S.isFavorite)}>
                    {displayFavoritesList ? (
                        hasVariants ? (
                            // If the product has variants, show the link to the favorites list
                            isFavorite ? (
                                <DA_Button
                                    href={favoritesListUrl}
                                    icon={DA_IconNames.HeartSolid}
                                    disabled={loading}
                                />
                            ) : (
                                <DA_Button
                                    href={getLocalUrl(url)}
                                    icon={DA_IconNames.HeartOutline}
                                    disabled={loading}
                                />
                            )
                        ) : (
                            // If the product does not have variants, show a button to add/remove it directly
                            <DA_Button
                                onClick={handleFavoriteToggle}
                                icon={
                                    isFavorite ? DA_IconNames.HeartSolid : DA_IconNames.HeartOutline
                                }
                                disabled={loading}
                            />
                        )
                    ) : null}
                </div>

                <DA_BasicLink
                    href={discountedVariantUrl || getLocalUrl(url)}
                    className={S.imageLink}
                    onClick={onLinkClicked}
                >
                    {
                        <div className={S.productImageWrapper}>
                            {/* TODO: Develop new image component */}
                            <DA_Image
                                src={B2bImageSrc(imageUrl, ImageConfig.CategoryImage)}
                                alt={dataName}
                                loading={eagerLoading ? "eager" : "lazy"}
                                fetchpriority={props.fetchPriority ? "high" : "auto"}
                                className={S.productImage}
                            />
                        </div>
                    }
                </DA_BasicLink>

                {/* {newProduct && (
                        <DA_Badge
                        className={cn(S.badge, S.newProductBadge)}
                        size="normal"
                        text={getDictionaryString("new splat")}
                        />
                    )}

                    {videos && videos.length > 0 ? (
                        <DA_BasicLink href={`${url}#video`} className={S.videoLink}>
                        <DA_Icon style={{ color: "#fff" }} name={DA_IconNames.Play} />
                        </DA_BasicLink>
                    ) : null} */}
            </div>

            <div className={S.productDataWrapper}>
                <div>
                    <div className={S.productNumber}>
                        <DA_Text noPadding smallText>
                            {`${getDictionaryString("product number")} ${props.productNumber}`}
                        </DA_Text>
                    </div>
                    <div className={S.productName}>
                        <DA_BasicLink
                            href={discountedVariantUrl || getLocalUrl(url)}
                            className={S.productNameLink}
                        >
                            <span itemProp="name">{displayName}</span>
                        </DA_BasicLink>
                    </div>
                    {hasVariants && (
                        <div className={S.lightText}>{getDictionaryString("more variants")}</div>
                    )}
                </div>

                <div className={S.secondaryContent}>
                    {/** Energy Classification */}
                    {props.data?.energyEfficiencyArrows?.value &&
                        props.data?.energyEfficiencyReport?.value && (
                            <div className={S.energyWrapper}>
                                <DA_BasicLink href={getLocalUrl(url)}>
                                    <img
                                        className={S.energyImage}
                                        src={props.data?.energyEfficiencyArrows?.value}
                                        alt=""
                                    />
                                </DA_BasicLink>
                                <DA_InlineLink
                                    className={S.energyLabel}
                                    href={props.data?.energyEfficiencyReport?.value}
                                    title={getDictionaryString("ProductSheet")}
                                    target="_blank"
                                />
                            </div>
                        )}

                    {/** Price */}
                    {showPrices && isAvailableToBuy && !hidePrice ? (
                        <div>
                            {(props.hasDiscount ||
                                (salesEndDate && Object.entries(salesEndDate).length > 0)) && (
                                <DA_Tag color="yellow" size="large">
                                    {getDictionaryString("offer splat")}
                                </DA_Tag>
                            )}
                            <DA_ProductListPrice
                                price={props.price}
                                quantity={quantity}
                                hasVariants={hasVariants}
                                discount={salesEndDate}
                                onClick={
                                    isMobileOrTablet ? openDynamicOverlay : openShippingInfoModal
                                }
                            />
                        </div>
                    ) : null}

                    <div className={S.pushContent}>
                        {/** Quantity */}
                        {isAvailableToBuy ? (
                            <DA_QuantityPicker
                                value={quantity}
                                onChange={setQuantity}
                                compactOnMobile
                                higherMax={highMax}
                                minQty={minimumSalesQuantity}
                            />
                        ) : null}

                        {/** Button */}
                        <DA_CartButton
                            // title={getDictionaryString("add to cart")}
                            hasVariants={hasVariants}
                            hasHazardStatements={hasHazardStatement}
                            isAvailableToBuy={isAvailableToBuy}
                            productPage={discountedVariantUrl || getLocalUrl(url)}
                            hideText={isMobileOrTablet}
                            addToCart={() =>
                                addProductToCart(
                                    modalViewState,
                                    quantityAsInt,
                                    totalPalletQuantity,
                                    numberOfPallets
                                )
                            }
                            showSpinner={awaitingResponse}
                            fullWidth={isMobileOrTablet}
                            className={S.cartSliderBuyBtn}
                            ariaTitle={displayName}
                        />
                    </div>
                </div>
            </div>
        </article>
    );
});

V1.displayName = "V1";
